import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectFirebaseJWT, selectAccount } from '../../redux/features/user/userSlice';
import {QRCodeSVG} from 'qrcode.react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import axios from 'axios';
import './TwoFactorAuthSetup.css';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import useUserRole from '../../utils/useUserRole';
import use2FAStatus from '../../hooks/use2FAStatus';

const API_BASE = 'https://api-v2.pesabase.com/api/account';

// Fetcher for SWR
const fetcher = (url, jwt) =>
  axios.get(url, {
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      'accept': 'application/json',
    },
  }).then(res => {
    return res.data;
  }).catch(error => {
    throw new Error('Failed to generate TOTP');
  });

// Mutation for verify-totp
const verifyTOTP = async (url, { arg }) => {
  const { jwt, token } = arg;
  const res = await axios.post(url, { token }, {
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      'accept': 'application/json',
    },
  });
  return res.data;
};

// Mutation for setup-pin (for agents)
const setupPIN = async (url, { arg }) => {
  const { jwt, pin, userId } = arg;
  
  try {
    const res = await axios.post(url, { pin, userId }, {
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
        'accept': 'application/json',
      },
    });
    
    // Debug: Log the full response
    console.log('PIN setup response status:', res.status);
    console.log('PIN setup response data:', res.data);
    
    // Check if the response status is 200
    if (res.status !== 200) {
      throw new Error(`PIN setup failed: ${res.data.message || 'Server error'}`);
    }
    
    return res.data;
  } catch (error) {
    // Handle axios errors
    if (error.response) {
      // Server responded with error status
      console.log('PIN setup error response:', error.response);
      throw new Error(`PIN setup failed: ${error.response.data?.message || 'Server error'}`);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('PIN setup failed: No response from server');
    } else {
      // Something else happened
      throw new Error(`PIN setup failed: ${error.message}`);
    }
  }
};

// Mutation for verify-pin (for agents) - removed, verification happens on frontend

const TwoFactorAuthSetup = () => {
  const jwt = useSelector(selectFirebaseJWT);
  const userAccount = useSelector(selectAccount);
  const navigate = useNavigate();
  const [step, setStep] = useState('intro');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '', '', '']);
  const [activeInput, setActiveInput] = useState(0);
  const [copied, setCopied] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const inputRefs = useRef([]);
  const pinInputRefs = useRef([]);
  const confirmPinInputRefs = useRef([]);
  const userRole = useUserRole();
  const { isSecurityRequired, loading: loading2FAStatus } = use2FAStatus();
  const isAgent = userRole === 'agent';

  // If security is already set up, show the success screen immediately
  useEffect(() => {
    if (!loading2FAStatus && !isSecurityRequired) {
      setStep('success');
    }
  }, [isSecurityRequired, loading2FAStatus]);

  // SWR for generate-totp (only fetch when step === 'qr' and user is not agent)
  const shouldFetch = step === 'qr' && !!jwt && !isAgent;
  const { data: qrData, error: qrError, isLoading: qrLoading, mutate: refetchQR } = useSWR(
    shouldFetch ? [API_BASE + '/generate-totp', jwt] : null,
    ([url, jwt]) => fetcher(url, jwt),
    { revalidateOnFocus: false }
  );

  // SWR mutation for verify-totp (for regular users)
  const { trigger: triggerVerify, isMutating: isVerifying } = useSWRMutation(
    API_BASE + '/verify-totp',
    verifyTOTP
  );

  // SWR mutation for setup-pin (for agents)
  const { trigger: triggerSetupPIN, isMutating: isSettingUpPIN } = useSWRMutation(
    API_BASE + '/pin/setup',
    setupPIN
  );

  // SWR mutation for verify-pin (for agents) - removed, verification happens on frontend

  // Handle Enable 2FA button
  const handleEnable2FA = () => {
    if (isAgent) {
      setStep('setup-pin');
    } else {
      setStep('qr');
    }
  };

  // Handle OTP input change (for regular users)
  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    // If user pastes a 6-digit code
    if (value.length === 6 && /^\d{6}$/.test(value)) {
      setOtp(value.split(''));
      setActiveInput(5);
      inputRefs.current[5]?.focus();
      return;
    }
    // Only allow numbers and limit to 1 character
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        setActiveInput(index + 1);
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  // Handle PIN input change (for agents)
  const handlePinChange = (e, index, isConfirm = false) => {
    const value = e.target.value;
    const pinArray = isConfirm ? confirmPin : pin;
    const setPinArray = isConfirm ? setConfirmPin : setPin;
    const refs = isConfirm ? confirmPinInputRefs : pinInputRefs;
    
    // If user pastes a 6-digit code
    if (value.length === 6 && /^\d{6}$/.test(value)) {
      setPinArray(value.split(''));
      setActiveInput(5);
      refs.current[5]?.focus();
      return;
    }
    // Only allow numbers and limit to 1 character
    if (/^\d?$/.test(value)) {
      const newPin = [...pinArray];
      newPin[index] = value;
      setPinArray(newPin);
      if (value && index < 5) {
        setActiveInput(index + 1);
        refs.current[index + 1]?.focus();
      }
    }
  };

  // Handle key events for OTP inputs
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        setActiveInput(index - 1);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setActiveInput(index - 1);
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      setActiveInput(index + 1);
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle key events for PIN inputs
  const handlePinKeyDown = (e, index, isConfirm = false) => {
    const refs = isConfirm ? confirmPinInputRefs : pinInputRefs;
    const pinArray = isConfirm ? confirmPin : pin;
    
    if (e.key === 'Backspace') {
      if (!pinArray[index] && index > 0) {
        setActiveInput(index - 1);
        refs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setActiveInput(index - 1);
      refs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      setActiveInput(index + 1);
      refs.current[index + 1]?.focus();
    }
  };

  // Handle setup PIN (for agents) - just validate and move to confirmation
  const handleSetupPIN = () => {
    const pinString = pin.join('');
    if (pinString.length !== 6) {
      toast.error('Please enter a 6-digit PIN');
      return;
    }
    console.log('Moving to confirmation step with PIN:', pinString);
    // Just move to confirmation step, don't call API yet
    setStep('confirm-pin');
  };

  // Handle confirm PIN (for agents)
  const handleConfirmPIN = async () => {
    const pinString = pin.join('');
    const confirmPinString = confirmPin.join('');
    
    if (confirmPinString.length !== 6) {
      toast.error('Please enter a 6-digit confirmation PIN');
      return;
    }
    
    if (pinString !== confirmPinString) {
      toast.error('PINs do not match. Please try again.');
      return;
    }
    
    console.log('Calling API to set up PIN for user:', userAccount?.uid);
    
    try {
      const data = await triggerSetupPIN({ 
        jwt, 
        pin: confirmPinString, 
        userId: userAccount?.uid 
      });
      console.log('PIN setup response:', data);
      
      // Since we're now checking for 200 status in setupPIN, 
      // if we reach here, the request was successful
      setBackupCodes(data.backupCodes || []);
      setStep('success');
      
    } catch (error) {
      console.error('PIN setup error:', error);
      toast.error(`Could not set up PIN: ${error.message || 'Please try again.'}`);
    }
  };

  // Handle verify 2FA (for regular users)
  const handleVerify2FA = async () => {
    const token = otp.join('');
    if (token.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    try {
      const data = await triggerVerify({ jwt, token });
      if ((data.success || data.status === 200) && data.backupCodes) {
        setBackupCodes(data.backupCodes);
        setStep('success');
      } else if (data.success || data.status === 200) {
        setBackupCodes([]);
        setStep('success');
      } else {
        toast.error('The code you entered is incorrect');
      }
    } catch (error) {
      toast.error('Could not verify your code. Please try again.');
    }
  };

  // Reset copied state after 3 seconds
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  // Handle finish setup
  const handleFinishSetup = () => {
    toast.success('2FA setup complete!');
    setTimeout(() => {
      navigate(userRole === 'agent' ? '/agent/dashboard' : '/user/dashboard');
    }, 1000);
  };

  // Render intro screen
  const renderIntroScreen = () => (
    <div className="twofa-intro-card">
      <h6 className="el-title-h6 med">Two-Factor Authentication</h6>
      <p className="el-text-m">
        {isAgent 
          ? "Add an extra layer of security to your agent account. With 2FA enabled, you'll need to enter a 6-digit PIN each time you sign in."
          : "Add an extra layer of security to your account. With 2FA enabled, you'll need to enter a verification code from your phone each time you sign in."
        }
      </p>
      <ul className="el-text-m" style={{margin: '16px 0'}}>
        <li>🔒 Enhanced account security</li>
        {isAgent ? (
          <>
            <li>🔢 6-digit PIN protection</li>
            <li>🔄 Required for sensitive actions</li>
          </>
        ) : (
          <>
            <li>📱 Codes generated on your device</li>
            <li>🔄 Required for sensitive actions</li>
          </>
        )}
      </ul>
      <button 
        className={`el-button orange${qrLoading ? ' loading' : ''}`} 
        onClick={handleEnable2FA}
        disabled={qrLoading}
        style={{marginTop: 16}}
      >
        {qrLoading ? 'Loading...' : 'Enable 2FA'}
      </button>
      {qrError && <div className="el-text-s" style={{color: 'red'}}>Failed to generate QR. Please try again.</div>}
    </div>
  );

  // Render PIN setup screen (for agents)
  const renderPINSetupScreen = () => (
    <div className="twofa-card">
      <h6 className="el-title-h6 med">Set Up Security PIN</h6>
      <p className="el-text-m">
        Create a 6-digit PIN that you'll use to secure your account
      </p>
      <div style={{margin: '32px 0'}}>
        <h6 className="el-title-h6">Enter your 6-digit PIN</h6>
        <div style={{display: 'flex', gap: 8, margin: '16px 0'}}>
          {pin.map((digit, index) => (
            <input
              key={index}
              type="password"
              maxLength={6}
              value={digit}
              onChange={(e) => handlePinChange(e, index)}
              onKeyDown={(e) => handlePinKeyDown(e, index)}
              onFocus={() => setActiveInput(index)}
              className={`el-text-m med el-input${activeInput === index ? ' active' : ''}`}
              style={{width: 32, height: 40, textAlign: 'center', fontSize: 20, border: '1px solid #d3d6d8', borderRadius: 8}}
              autoFocus={index === activeInput}
              ref={el => pinInputRefs.current[index] = el}
            />
          ))}
        </div>
        <p className="el-text-s" style={{color: '#666'}}>
          Choose a PIN that's easy to remember but hard to guess
        </p>
      </div>
      <div style={{display: 'flex', gap: 12}}>
        <button className="el-button" onClick={() => setStep('intro')}>Back</button>
        <button 
          className="el-button orange"
          onClick={handleSetupPIN}
        >
          Continue
        </button>
      </div>
    </div>
  );

  // Render PIN confirmation screen (for agents)
  const renderPINConfirmScreen = () => (
    <div className="twofa-card">
      <h6 className="el-title-h6 med">Confirm Your PIN</h6>
      <p className="el-text-m">
        Please enter your 6-digit PIN again to confirm
      </p>
      <div style={{margin: '32px 0'}}>
        <h6 className="el-title-h6">Re-enter your 6-digit PIN</h6>
        <div style={{display: 'flex', gap: 8, margin: '16px 0'}}>
          {confirmPin.map((digit, index) => (
            <input
              key={index}
              type="password"
              maxLength={6}
              value={digit}
              onChange={(e) => handlePinChange(e, index, true)}
              onKeyDown={(e) => handlePinKeyDown(e, index, true)}
              onFocus={() => setActiveInput(index)}
              className={`el-text-m med el-input${activeInput === index ? ' active' : ''}`}
              style={{width: 32, height: 40, textAlign: 'center', fontSize: 20, border: '1px solid #d3d6d8', borderRadius: 8}}
              autoFocus={index === activeInput}
              ref={el => confirmPinInputRefs.current[index] = el}
            />
          ))}
        </div>
        <p className="el-text-s" style={{color: '#666'}}>
          This ensures you remember your PIN correctly
        </p>
      </div>
      <div style={{display: 'flex', gap: 12}}>
        <button className="el-button" onClick={() => setStep('setup-pin')}>Back</button>
        <button 
          className={`el-button orange${isSettingUpPIN ? ' loading' : ''}`}
          onClick={handleConfirmPIN}
          disabled={isSettingUpPIN}
        >
          {isSettingUpPIN ? 'Setting up...' : 'Verify and Enable'}
        </button>
      </div>
    </div>
  );

  // Render QR screen (for regular users)
  const renderQRScreen = () => (
    <div className="twofa-card">
      <h6 className="el-title-h6 med">Set Up Authenticator</h6>
      <p className="el-text-m">
        Scan this QR code with Google Authenticator or Authy
      </p>
      <div style={{margin: '32px 0'}}>
        {qrLoading ? (
          <div className="el-text-m">Loading QR...</div>
        ) : qrData ? (
          qrData.qrCode && qrData.qrCode.startsWith('data:image') ? (
            <img src={qrData.qrCode} alt="TOTP QR Code" className="twofa-qr-img" />
          ) : (
            <QRCodeSVG 
              value={qrData.qrCode || qrData.qr_code || ''}
              width={360}
              height={180}
              size={180}
              bgColor="#ffffff"
              fgColor="#000000"
              className="twofa-qr-img"
            />
          )
        ) : null}
      </div>
      <h6 className="el-title-h6">Can't scan the QR code?</h6>
      <p className="el-text-m">
        Click below to copy the secret key and enter it manually in your authenticator app:
      </p>
      <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8}}>
        <CopyToClipboard 
          text={qrData?.secret || ''} 
          onCopy={() => setCopied(true)}
        >
          <button className="el-button" style={{padding: '8px 18px', fontSize: 16}}>
            {copied ? 'Copied!' : 'Copy Secret Key'}
          </button>
        </CopyToClipboard>
      </div>
      <p className="el-text-s" style={{marginBottom: 16}}>
        Note: Make sure time synchronization is enabled in your authenticator app
      </p>
      <div style={{display: 'flex', gap: 12}}>
        <button className="el-button" onClick={() => setStep('intro')}>Back</button>
        <button className="el-button orange" onClick={() => setStep('verify')}>I've Set Up Authenticator</button>
      </div>
    </div>
  );

  // Render verify screen (for regular users)
  const renderVerifyScreen = () => (
    <div className="twofa-card">
      <h6 className="el-title-h6 med">Verify Authenticator</h6>
      <p className="el-text-m">
        Enter the 6-digit code from your authenticator app
      </p>
      <div style={{display: 'flex', gap: 8, margin: '16px 0'}}>
        {otp.map((digit, index) => (
          <input
            key={index}
            type="text"
            maxLength={6}
            value={digit}
            onChange={(e) => handleOtpChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onFocus={() => setActiveInput(index)}
            className={`el-text-m med el-input${activeInput === index ? ' active' : ''}`}
            style={{width: 32, height: 40, textAlign: 'center', fontSize: 20, border: '1px solid #d3d6d8', borderRadius: 8}}
            autoFocus={index === activeInput}
            ref={el => inputRefs.current[index] = el}
          />
        ))}
      </div>
      <div style={{display: 'flex', gap: 12}}>
        <button className="el-button" onClick={() => setStep('qr')}>Back</button>
        <button 
          className={`el-button orange${isVerifying ? ' loading' : ''}`}
          onClick={handleVerify2FA}
          disabled={isVerifying}
        >
          {isVerifying ? 'Verifying...' : 'Verify and Enable'}
        </button>
      </div>
    </div>
  );

  // Render success screen
  const renderSuccessScreen = () => (
    <div className="settings-box-el">
      <div style={{fontSize: 40, color: 'var(--color-orange-main)', marginBottom: 8}}>✓</div>
      <h6 className="el-title-h6 med">Two-Factor Enabled!</h6>
      <p className="el-text-m">
        Your account is now protected with two-factor authentication.
      </p>
      {!isAgent && (
        <>
          <h6 className="el-title-h6">Backup Codes</h6>
          <p className="el-text-s">
            Save these codes in a safe place. You can use them to access your account if you lose your authenticator app.
          </p>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, margin: '12px 0'}}>
            {backupCodes && backupCodes.length > 0 ? (
              backupCodes.map((code, index) => (
                <div key={index} className="el-text-m med" style={{background: '#f5f5f5', borderRadius: 6, padding: '6px 12px', fontFamily: 'monospace'}}>{code}</div>
              ))
            ) : (
              <div className="el-text-m med" style={{color: '#888'}}>No backup codes returned.</div>
            )}
          </div>
        </>
      )}
      <div style={{display: 'flex', gap: 12}}>
        {!isAgent && <button className="el-button">Download Backup Codes</button>}
        <button className="el-button orange" onClick={handleFinishSetup}>Finish Setup</button>
      </div>
    </div>
  );

  return (
    <div>
      {step === 'intro' && renderIntroScreen()}
      {step === 'setup-pin' && isAgent && renderPINSetupScreen()}
      {step === 'confirm-pin' && isAgent && renderPINConfirmScreen()}
      {step === 'qr' && !isAgent && renderQRScreen()}
      {step === 'verify' && !isAgent && renderVerifyScreen()}
      {step === 'success' && renderSuccessScreen()}
    </div>
  );
};

export default TwoFactorAuthSetup; 